import React, { useRef, useState } from 'react';
import AddSecurityRow from './AddSecurityRow';
import PendingAdditionCell from './PendingAdditionCell';
import {
    DEFAULT_MAX_SECURITIES,
    addPick,
    pickHistoryAction,
    remainingCapacity,
    removePick,
} from './pendingAdditions';

function sameCapacity(left, right) {
    return left.max_securities === right.max_securities
        && left.current_security_count === right.current_security_count;
}

/**
 * The owner's side of the grid's "+ Add security" row (WatchlistGrid's
 * `pendingRows` and `addRow`). Picks go through the picks model; each change
 * is one undo step, `onPicksChange(action)` with a pickHistoryAction: the
 * owner applies `action.picks.after` and records the action in its draft
 * history, so Undo reverses picks and cell edits in order.
 *
 * Capacity is the server's, as a search last reported it, while
 * `capacityKey` (the published Watchlist) stays the same; otherwise
 * `maxSecurities` less the `publishedCount` securities. `existingCogniveIds`
 * keeps out a pick the server identified as a current member; the server's
 * `already_active` does that for search results. `onExisting(security)` is
 * the editor's "Show it", `onUndo` / `onRedo` the owner's Undo and Redo,
 * and `searchApi` replaces the backend search.
 */
export default function useAddSecurityGridRows({
    picks,
    onPicksChange,
    publishedCount,
    capacityKey = '',
    maxSecurities = DEFAULT_MAX_SECURITIES,
    existingCogniveIds = null,
    disabled = false,
    onExisting = null,
    onUndo = null,
    onRedo = null,
    searchApi = undefined,
}) {
    const [reported, setReported] = useState(null);
    // The newest picks, for a second pick that lands before the next render.
    const picksRef = useRef(picks);
    picksRef.current = picks;

    const serverCapacity = reported?.key === capacityKey ? reported.capacity : null;
    const capacity = serverCapacity
        ? { maxSecurities: serverCapacity.max_securities, currentCount: serverCapacity.current_security_count }
        : { maxSecurities, currentCount: publishedCount };
    const remaining = remainingCapacity(picks, capacity);

    const change = (before, after, label) => {
        if (after === before) return;
        picksRef.current = after;
        onPicksChange(pickHistoryAction(before, after, label));
    };
    const handlePick = (pick) => {
        const before = picksRef.current;
        const result = addPick(before, pick, { ...capacity, existingCogniveIds });
        change(before, result.picks, 'Add security');
        return result.outcome;
    };
    const handleRemove = (key) => {
        const before = picksRef.current;
        change(before, removePick(before, key), "Don't add");
    };
    const handleCapacityChange = (next) => {
        setReported((current) => (
            current?.key === capacityKey && sameCapacity(current.capacity, next)
                ? current
                : { key: capacityKey, capacity: next }
        ));
    };

    return {
        remainingCapacity: remaining,
        pendingRows: picks.map((pick) => ({
            id: pick.key,
            content: <PendingAdditionCell pick={pick} onRemove={handleRemove} disabled={disabled} />,
        })),
        addRow: {
            disabled: disabled || remaining <= 0,
            render: (controls) => (
                <AddSecurityRow
                    {...controls}
                    disabled={disabled}
                    remainingCapacity={remaining}
                    pendingPicks={picks}
                    onPick={handlePick}
                    onExisting={onExisting}
                    onCapacityChange={handleCapacityChange}
                    onUndo={onUndo}
                    onRedo={onRedo}
                    searchApi={searchApi}
                />
            ),
        },
    };
}
